import React, { useState } from "react";
import PropTypes from "prop-types";

import { createDroppable, DraggableFragment } from "components/DragDrop";

import {
  DragDropContext,
  Form,
  FormField,
  FormFieldBuilder,
  FormLayout,
  useBooleanState,
  useMappedBooleanState,
} from "./components";

const DroppableFieldList = createDroppable("FormFieldList");

export function FormFieldList({
  formName,
  initialAddMode,
  isFormInstance,
  isDeletingForm,
  isUpdatingForm,
  isCreatingField,
  fields,
  onAddField,
  onEditField,
  onDeleteField,
  onUpdateForm,
  onDeleteForm,
  onClose,
  onFieldReordered,
  onClick,
}) {
  const [isAddMode, openAddMode, closeAddMode] =
    useBooleanState(initialAddMode);
  const [editedFields, openEditMode, closeEditMode] = useMappedBooleanState();
  const [isAddAboveMode, openAddAboveMode, closeAddAboveMode] =
    useMappedBooleanState();
  const isAddingFieldAbove = Object.values(isAddAboveMode).some((value) => {
    return value;
  });

  const [watchFormName, setChangeFormName] = useState("");

  return (
    <DragDropContext onDragEnd={onFieldReordered}>
      <Form
        loading={isDeletingForm || isUpdatingForm || isCreatingField}
        onUpdate={onUpdateForm}
        formName={formName}
        onDelete={onDeleteForm}
        onAddField={openAddMode}
        hideMenu={isFormInstance}
        onClick={onClick}
        initialAddMode={initialAddMode}
        onChangeFormName={setChangeFormName}
      >
        <DroppableFieldList id="form-field-dnd-list">
          {fields.map((f, i) => {
            return (
              <React.Fragment key={f.id}>
                {isAddAboveMode[f.id] && (
                  <DraggableFragment
                    id="form-field-add-above"
                    index={i}
                    disabled
                  >
                    <FormLayout icon="clipboard">
                      <FormFieldBuilder
                        formName={formName}
                        loading={isCreatingField}
                        onSubmit={(field) => {
                          return onAddField(field, f.id);
                        }}
                        onClose={() => {
                          closeAddAboveMode(f.id);
                        }}
                      />
                    </FormLayout>
                  </DraggableFragment>
                )}

                <DraggableFragment
                  id={f.id}
                  index={i}
                  disabled={isFormInstance || isAddingFieldAbove}
                >
                  <FormField
                    field={f}
                    formName={formName}
                    value={isFormInstance ? f.value : f.options}
                    prefix={isFormInstance ? "" : "options"}
                    editMode={Boolean(editedFields[f.id]) && !isFormInstance}
                    hideMenu={isFormInstance}
                    onEditField={(v) => {
                      return onEditField(f, v);
                    }}
                    onEnableEditMode={() => {
                      openEditMode(f.id);
                    }}
                    inlineEditor={
                      <FormFieldBuilder
                        initialValues={f}
                        formName={formName}
                        onSubmit={async (v) => {
                          const resp = await onEditField(f, v);
                          if (resp) closeEditMode(f.id);
                          return resp;
                        }}
                        onClose={() => {
                          closeEditMode(f.id);
                        }}
                        loading={f.loading}
                      />
                    }
                    onDelete={() => {
                      onDeleteField(f);
                      closeAddAboveMode(f.id);
                    }}
                    onAddFormFieldAbove={() => {
                      openAddAboveMode(f.id);
                    }}
                  />
                </DraggableFragment>
              </React.Fragment>
            );
          })}
          {isAddMode && (
            <DraggableFragment
              id="form-field-add"
              index={fields.length}
              disabled
            >
              <FormLayout icon="clipboard">
                <FormFieldBuilder
                  formName={formName || watchFormName}
                  loading={isCreatingField}
                  onSubmit={(f) => {
                    return onAddField(
                      f,
                      null,
                      watchFormName.trim() !== "" ? watchFormName : null
                    );
                  }}
                  onClose={() => {
                    return fields.length === 0 ? onClose() : closeAddMode();
                  }}
                />
              </FormLayout>
            </DraggableFragment>
          )}
        </DroppableFieldList>
      </Form>
    </DragDropContext>
  );
}

FormFieldList.propTypes = {
  isUpdatingForm: PropTypes.bool,
  isDeletingForm: PropTypes.bool,
  isCreatingField: PropTypes.bool,
  initialAddMode: PropTypes.bool,
  formName: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.object,
      name: PropTypes.string,
      options: PropTypes.array,
      value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.instanceOf(Date),
      ]),
    })
  ),
  isFormInstance: PropTypes.bool,
  onAddField: PropTypes.func,
  onEditField: PropTypes.func,
  onUpdateForm: PropTypes.func,
  onDeleteForm: PropTypes.func,
  onDeleteField: PropTypes.func,
  onClose: PropTypes.func,
  onFieldReordered: PropTypes.func,
  onClick: PropTypes.func,
};

FormFieldList.defaultProps = {
  fields: [],
  initialAddMode: false,
  isUpdatingForm: false,
  isDeletingForm: false,
  isCreatingField: false,
  isFormInstance: false,
  formName: "",
  onAddField: () => {},
  onEditField: () => {},
  onUpdateForm: () => {},
  onDeleteForm: () => {},
  onDeleteField: () => {},
  onFieldReordered: () => {},
  onClose: () => {},
  onClick: () => {},
};

export default FormFieldList;
